/**
 * Access denied — /access-denied route.
 *
 * Terminal screen in the sign-up approval gate (see RequireApproved in
 * App.tsx) for a user whose request a platform admin declined
 * (approval_status='rejected'). Sign-out is the only action; if the decision
 * is reversed server-side, signing back in re-runs the gate and lets them
 * through.
 */

import { ShieldX, LogOut } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useTenantTheme } from "@/lib/tenant-theme";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";

export function AccessDenied() {
  const { logout } = useAuth();
  const tenant = useTenantTheme();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-10">
      <div className="mb-6 flex items-center gap-2">
        <BrandMark />
        <span className="text-base font-semibold text-on-surface">
          {tenant.productName}
        </span>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <Badge variant="danger" className="w-fit uppercase tracking-wide">
            <ShieldX className="mr-1 size-3" />
            Not approved
          </Badge>
          <CardTitle className="text-2xl">Access request not approved</CardTitle>
          <CardDescription>
            Your request to access {tenant.productName} wasn't approved. If you
            think this is a mistake, reach out to your platform administrator —
            they can review the decision.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end border-t border-border pt-4">
            <Button variant="ghost" size="sm" onClick={() => logout()}>
              <LogOut className="mr-1.5 size-4" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
